import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Search, RefreshCw, CheckCircle, XCircle, Clock, DollarSign, Filter, Truck, User, Package, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/use-toast';
import { adminAPI } from '@/services/api';
import DataTable from '@/components/ui/data-table';
import LogiConnectIdBadge from '@/components/ui/logiconnect-id-badge';

const BookingManagement = ({ transporterId, onStatisticsUpdate }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [statistics, setStatistics] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    completed: 0,
    cancelled: 0,
    totalValue: 0,
  });

  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status_code = statusFilter;

      const response = await adminAPI.getTransporterBookings(transporterId, params);
      const bookingsData = response.data.results || response.data || [];
      setBookings(bookingsData);

      const stats = {
        total: bookingsData.length,
        confirmed: bookingsData.filter(b => b.status_code === 'CONFIRMED').length,
        inProgress: bookingsData.filter(b => b.status_code === 'IN_PROGRESS').length,
        delivered: bookingsData.filter(b => b.status_code === 'DELIVERED').length,
        completed: bookingsData.filter(b => b.status_code === 'COMPLETED').length,
        cancelled: bookingsData.filter(b => b.status_code === 'CANCELLED').length,
        totalValue: bookingsData.reduce((sum, b) => sum + (Number(b.agreed_price) || 0), 0),
      };
      setStatistics(stats);

      if (onStatisticsUpdate) {
        onStatisticsUpdate(stats);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des réservations:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les réservations',
        variant: 'destructive',
      });
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [transporterId, statusFilter, onStatisticsUpdate]);

  useEffect(() => {
    if (transporterId) {
      loadInitialData();
    }
  }, [transporterId, loadInitialData]);

  const handleRefresh = async () => {
    await loadInitialData();
    toast({
      title: 'Succès',
      description: 'Données mises à jour',
    });
  };

  const filteredBookings = bookings.filter(booking => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      booking.id?.toLowerCase().includes(searchLower) ||
      booking.client?.company_name?.toLowerCase().includes(searchLower) ||
      booking.status_code?.toLowerCase().includes(searchLower)
    );
  });

  const columns = [
    {
      id: 'request',
      header: 'Demande',
      accessorKey: 'request',
      cell: ({ row }) => {
        const booking = row.original;
        const request = booking.request_detail || booking.request;

        if (!request) {
          return <span className="text-muted-foreground text-sm">N/A</span>;
        }

        // Essayer d'abord le logiconnect_id
        if (request.logiconnect_id) {
          return <LogiConnectIdBadge id={request.logiconnect_id} showLabel={false} size="sm" />;
        }

        // Sinon, utiliser l'ID de la demande (8 derniers caractères)
        if (request.id) {
          const requestId = typeof request.id === 'string' ? request.id : String(request.id);
          return (
            <span className="text-sm font-mono text-muted-foreground">
              #{requestId.slice(-8)}
            </span>
          );
        }

        return <span className="text-muted-foreground text-sm">N/A</span>;
      },
    },
    {
      id: 'client',
      header: 'Client',
      accessorKey: 'client',
      cell: ({ row }) => {
        const booking = row.original;
        const client = booking.client_detail || booking.client;
        const user = client?.user_detail || client?.user;
        return (
          <div className="text-sm">
            <p className="font-medium">
              {client?.company_name ||
               (user?.first_name && user?.last_name
                 ? `${user.first_name} ${user.last_name}`.trim()
                 : user?.first_name || 'N/A')}
            </p>
            {user?.email && (
              <p className="text-xs text-muted-foreground">{user.email}</p>
            )}
          </div>
        );
      },
    },
    {
      id: 'vehicle',
      header: 'Véhicule',
      accessorKey: 'vehicle',
      cell: ({ row }) => {
        const booking = row.original;
        const vehicle = booking.vehicle_detail || booking.vehicle;
        return (
          <div className="flex items-center gap-1 text-sm">
            <Truck className="w-4 h-4 text-muted-foreground" />
            <span>{vehicle?.plate_number || vehicle?.license_plate || 'N/A'}</span>
          </div>
        );
      },
    },
    {
      id: 'driver',
      header: 'Chauffeur',
      accessorKey: 'driver',
      cell: ({ row }) => {
        const booking = row.original;
        const driver = booking.driver_detail || booking.driver;
        return (
          <div className="flex items-center gap-1 text-sm">
            <User className="w-4 h-4 text-muted-foreground" />
            <span>
              {driver?.full_name ||
               (driver?.first_name && driver?.last_name
                 ? `${driver.first_name} ${driver.last_name}`.trim()
                 : driver?.first_name || 'N/A')}
            </span>
          </div>
        );
      },
    },
    {
      id: 'price',
      header: 'Prix',
      accessorKey: 'agreed_price',
      cell: ({ row }) => {
        const booking = row.original;
        return (
          <div className="flex flex-col gap-1 text-sm">
            <div className="flex items-center gap-1 font-medium">
              <DollarSign className="w-4 h-4 text-green-500" />
              <span className="text-primary">{booking.agreed_price ? Number(booking.agreed_price).toLocaleString('fr-FR') : '0'} GNF</span>
            </div>
            <div className="text-xs text-muted-foreground">
              Prix convenu
            </div>
            {booking.transporter_price && booking.transporter_price !== booking.agreed_price && (
              <>
                <div className="flex items-center gap-1 font-medium mt-1">
                  <DollarSign className="w-3 h-3 text-secondary" />
                  <span className="text-secondary">{Number(booking.transporter_price).toLocaleString('fr-FR')} GNF</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  Prix transporteur
                </div>
              </>
            )}
          </div>
        );
      },
    },
    {
      id: 'status',
      header: 'Statut',
      accessorKey: 'status_code',
      cell: ({ row }) => {
        const booking = row.original;
        const status = booking.status_code || 'CONFIRMED';
        const statusConfig = {
          CONFIRMED: { label: 'Confirmée', className: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: CheckCircle },
          IN_PROGRESS: { label: 'En cours', className: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: Clock },
          DELIVERED: { label: 'Livrée', className: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: Package },
          COMPLETED: { label: 'Terminée', className: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
          CANCELLED: { label: 'Annulée', className: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
        };
        const config = statusConfig[status] || statusConfig.CONFIRMED;
        const Icon = config.icon;
        return (
          <Badge className={config.className}>
            <Icon className="w-3 h-3 mr-1" />
            {config.label}
          </Badge>
        );
      },
    },
    {
      id: 'created_at',
      header: 'Créée le',
      accessorKey: 'created_at',
      cell: ({ row }) => {
        const booking = row.original;
        return (
          <span className="text-sm text-muted-foreground">
            {booking.created_at ? new Date(booking.created_at).toLocaleDateString('fr-FR') : 'N/A'}
          </span>
        );
      },
    },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-teal-500" />
            Réservations
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Statistiques */}
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-4">
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-2xl font-bold text-foreground">{statistics.total}</p>
            <p className="text-xs text-muted-foreground">Total</p>
          </div>
          <div className="bg-yellow-500/10 rounded-lg p-3">
            <p className="text-2xl font-bold text-yellow-500">{statistics.pending}</p>
            <p className="text-xs text-muted-foreground">En attente</p>
          </div>
          <div className="bg-blue-500/10 rounded-lg p-3">
            <p className="text-2xl font-bold text-blue-500">{statistics.confirmed}</p>
            <p className="text-xs text-muted-foreground">Confirmées</p>
          </div>
          <div className="bg-green-500/10 rounded-lg p-3">
            <p className="text-2xl font-bold text-green-500">{statistics.completed}</p>
            <p className="text-xs text-muted-foreground">Terminées</p>
          </div>
          <div className="bg-red-500/10 rounded-lg p-3">
            <p className="text-2xl font-bold text-red-500">{statistics.cancelled}</p>
            <p className="text-xs text-muted-foreground">Annulées</p>
          </div>
          <div className="bg-purple-500/10 rounded-lg p-3">
            <p className="text-sm font-bold text-purple-500">
              {statistics.totalValue.toLocaleString('fr-FR')} GNF
            </p>
            <p className="text-xs text-muted-foreground">Valeur totale</p>
          </div>
        </div>

        <Separator />

        {/* Filtres */}
        {showFilters && (
          <div className="bg-muted/30 rounded-lg p-4 space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    placeholder="Rechercher par ID, client..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm"
              >
                <option value="all">Tous les statuts</option>
                <option value="CONFIRMED">Confirmée</option>
                <option value="IN_PROGRESS">En cours</option>
                <option value="DELIVERED">Livrée</option>
                <option value="COMPLETED">Terminée</option>
                <option value="CANCELLED">Annulée</option>
              </select>
            </div>
          </div>
        )}

        {/* Tableau */}
        <div className="min-h-[400px]">
          <DataTable
            data={filteredBookings}
            columns={columns}
            loading={loading}
            searchable={false}
            pageSize={10}
            actions={[
              {
                label: 'Voir détails',
                icon: Eye,
                variant: 'ghost',
                onClick: (booking) => {
                  const request = booking.request_detail || booking.request;
                  const client = booking.client_detail || booking.client;
                  if (request?.id && client?.id) {
                    navigate(`/admin/clients/${client.id}/requests/${request.id}`);
                  }
                },
              },
            ]}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default BookingManagement;
